import { Chip } from '@mui/material';
import { useGetContentPosts } from 'api/services/SupplierPortal/ContentManagementService';
import { TableLoadData } from 'components/common/ReusableMaterialReactTable';
import { MRT_ColumnDef } from 'material-react-table';
import {
  ContentPost,
  UXBContentCategory,
  ContentPostStatus
} from 'portals/types';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { UXB_EVENT_KEY } from 'supplier-portals/uxbeauty/constants';

const useTableHelper = <T,>(
  contentCategory: UXBContentCategory,
  getTitle: (row: ContentPost<T>) => string,
  additionalColumns?: MRT_ColumnDef<ContentPost<T>>[]
) => {
  const [, refetchContent] = useGetContentPosts<T>(
    UXB_EVENT_KEY,
    contentCategory
  );

  const columns: MRT_ColumnDef<ContentPost<T>>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        accessorFn: row => getTitle(row),
        muiTableHeadCellProps: {
          sx: { '& .Mui-TableHeadCell-Content': { ml: 4 } }
        },
        muiTableBodyCellProps: {
          sx: {
            pl: 5,
            // To override from the custom css theme: td { white-space: break-spaces !important; }
            whiteSpace: 'nowrap !important'
          }
        }
      },
      ...(additionalColumns ?? []),
      {
        accessorKey: 'visibility',
        header: 'Visibility',
        accessorFn: (row: any) => {
          return (
            <Chip
              label={
                row.status === ContentPostStatus.PUBLISHED
                  ? ContentPostStatus.PUBLISHED
                  : ContentPostStatus.DRAFT
              }
              size="small"
              sx={{
                backgroundColor:
                  row.status === ContentPostStatus.PUBLISHED
                    ? 'success.main'
                    : 'grey.300',
                color:
                  row.status === ContentPostStatus.PUBLISHED ? 'white' : 'black'
              }}
            />
          );
        }
      }
    ],
    [additionalColumns, getTitle]
  );

  const loadData = useCallback<TableLoadData<ContentPost<T>>>(
    (setData, setRowCount, setLoading, commonParams) => {
      refetchContent({
        params: {
          size: commonParams.size,
          page: commonParams.page,
          sort: 'publish_date,desc'
        }
      })
        .then(res => {
          setData(res.data.content);
          setRowCount(res.data.page.total_elements);
          setLoading(false);
        })
        .catch(() => {
          toast.error('Failed to load events');
          setLoading(false);
        });
    },
    [refetchContent]
  );

  return {
    columns,
    loadData
  };
};

export default useTableHelper;
